import { useState } from 'react';
import { Plus, BookOpen, Clock, CheckCircle, AlertCircle, Calendar, GraduationCap, Star } from 'lucide-react';

interface Child {
  id: string;
  name: string;
  grade: string;
  school: string;
  color: string;
}

interface Homework {
  id: string;
  childId: string;
  subject: string;
  title: string;
  description: string;
  dueDate: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  attachments: string[];
}

const subjects = {
  math: { icon: '🔢', name: 'Matemáticas', color: 'bg-blue-100 text-blue-700' },
  science: { icon: '🔬', name: 'Ciencias', color: 'bg-green-100 text-green-700' },
  spanish: { icon: '📖', name: 'Español', color: 'bg-yellow-100 text-yellow-700' },
  history: { icon: '🏛️', name: 'Historia', color: 'bg-amber-100 text-amber-700' },
  english: { icon: '🇬🇧', name: 'Inglés', color: 'bg-purple-100 text-purple-700' },
  art: { icon: '🎨', name: 'Arte', color: 'bg-pink-100 text-pink-700' },
  pe: { icon: '⚽', name: 'Educación Física', color: 'bg-orange-100 text-orange-700' },
  other: { icon: '📚', name: 'Otro', color: 'bg-gray-100 text-gray-700' },
};

export default function KidsHomework() {
  const [children, setChildren] = useState<Child[]>([
    { id: '1', name: 'Sofía', grade: '3° Primaria', school: 'Colegio Bosques', color: 'bg-pink-500' },
    { id: '2', name: 'Mateo', grade: '1° Secundaria', school: 'Instituto Juárez', color: 'bg-blue-500' },
  ]);

  const [homework, setHomework] = useState<Homework[]>([
    { 
      id: '1', 
      childId: '1', 
      subject: 'math', 
      title: 'Ejercicios de multiplicación', 
      description: 'Página 45, ejercicios 1-20', 
      dueDate: '2026-03-18', 
      completed: false, 
      priority: 'medium',
      attachments: []
    },
    { 
      id: '2', 
      childId: '2', 
      subject: 'science', 
      title: 'Proyecto: El ciclo del agua', 
      description: 'Hacer una maqueta o presentación sobre el ciclo del agua', 
      dueDate: '2026-03-20', 
      completed: false, 
      priority: 'high',
      attachments: []
    },
    { 
      id: '3', 
      childId: '1', 
      subject: 'spanish', 
      title: 'Lectura: Don Quijote', 
      description: 'Leer capítulo 3 y responder cuestionario', 
      dueDate: '2026-03-17', 
      completed: true, 
      priority: 'low',
      attachments: []
    },
  ]);

  const [activeChild, setActiveChild] = useState<string>('all');

  const toggleHomework = (id: string) => {
    setHomework(homework.map(h => h.id === id ? { ...h, completed: !h.completed } : h));
  };

  const filteredHomework = activeChild === 'all'
    ? homework.filter(h => !h.completed).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    : homework.filter(h => h.childId === activeChild && !h.completed).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

  const getDaysUntil = (date: string) => {
    const diff = new Date(date).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Children Selector */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveChild('all')}
          className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${
            activeChild === 'all' 
              ? 'bg-blue-600 text-white' 
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          Todos los hijos
        </button>
        {children.map(child => (
          <button
            key={child.id}
            onClick={() => setActiveChild(child.id)}
            className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition flex items-center gap-2 ${
              activeChild === child.id 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            <div className={`w-6 h-6 rounded-full ${child.color}`} />
            {child.name}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
          <p className="text-2xl font-bold text-yellow-400">{homework.filter(h => !h.completed).length}</p>
          <p className="text-slate-400 text-xs">Pendientes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
          <p className="text-2xl font-bold text-red-400">{homework.filter(h => !h.completed && getDaysUntil(h.dueDate) <= 1).length}</p>
          <p className="text-slate-400 text-xs">Urgentes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
          <p className="text-2xl font-bold text-green-400">{homework.filter(h => h.completed).length}</p>
          <p className="text-slate-400 text-xs">Completadas</p>
        </div>
      </div>

      {/* Add Homework Button */}
      <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
        <Plus className="w-5 h-5" />
        Agregar Tarea
      </button>

      {/* Homework List */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-400" />
          Tareas Pendientes
        </h3>
        
        {filteredHomework.map((hw) => {
          const child = children.find(c => c.id === hw.childId);
          const subject = subjects[hw.subject as keyof typeof subjects];
          const daysLeft = getDaysUntil(hw.dueDate);
          
          return (
            <div
              key={hw.id}
              className={`bg-[var(--v-surface-1)] p-4 rounded-xl border ${
                daysLeft < 0 ? 'border-red-500' : daysLeft <= 1 ? 'border-yellow-500/50' : 'border-[var(--v-surface-3)]'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${subject.color}`}>
                  {subject.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-white font-semibold">{hw.title}</h4>
                    <span className="text-slate-400 text-sm">• {child?.name}</span>
                    <span className={`text-xs ${getPriorityColor(hw.priority)}`}>
                      {hw.priority === 'high' ? '● Alta' : hw.priority === 'medium' ? '● Media' : '● Baja'}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm mt-1">{hw.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`text-sm flex items-center gap-1 ${
                      daysLeft < 0 ? 'text-red-400 font-semibold' : daysLeft <= 1 ? 'text-yellow-400' : 'text-slate-400'
                    }`}>
                      <Calendar className="w-4 h-4" />
                      {daysLeft < 0 ? `Venció hace ${Math.abs(daysLeft)} días` : 
                       daysLeft === 0 ? '¡Vence hoy!' : 
                       daysLeft === 1 ? 'Mañana' : 
                       `${daysLeft} días`}
                    </span>
                    <span className="text-slate-500 text-sm">{subject.name}</span>
                  </div>
                </div>
                <button
                  onClick={() => toggleHomework(hw.id)}
                  className="w-10 h-10 rounded-full border-2 border-slate-600 hover:border-green-500 hover:bg-green-500/10 flex items-center justify-center transition"
                >
                  <CheckCircle className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
          );
        })}

        {filteredHomework.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Star className="w-12 h-12 mx-auto mb-2 text-yellow-500" />
            <p>¡Excelente trabajo!</p>
            <p className="text-sm">No hay tareas pendientes</p>
          </div>
        )}
      </div>

      {/* School Calendar */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
        <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-blue-400" />
          Próximos Eventos Escolares
        </h4>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
            <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center text-white font-bold text-sm">
              22
            </div>
            <div>
              <p className="text-white font-medium">Reunión de Padres</p>
              <p className="text-slate-400 text-sm">Colegio Bosques • 6:00 PM</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
            <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center text-white font-bold text-sm">
              25
            </div>
            <div>
              <p className="text-white font-medium">Exposición de Ciencias</p>
              <p className="text-slate-400 text-sm">Instituto Juárez • 10:00 AM</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
